import { useState, useEffect } from 'react'
import api from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import toast from 'react-hot-toast'
import {
  User,
  Mail,
  Phone,
  BookOpen,
  Calendar,
  Edit2,
  Save,
  X,
  Lock,
  Award,
  Briefcase,
  Users,
  Eye,
  EyeOff,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

function getInitials(first, last) {
  return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase()
}

export default function TrainerProfile() {
  const { user, updateUser } = useAuthStore()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  // Edit state
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)

  // Password change
  const [showPwSection, setShowPwSection] = useState(false)
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' })
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false })
  const [savingPw, setSavingPw] = useState(false)

  useEffect(() => {
    api.get('/trainers/profile')
      .then(res => {
        setProfile(res.data)
        setForm({
          first_name: res.data.first_name || '',
          last_name: res.data.last_name || '',
          phone: res.data.phone || '',
          specialization: res.data.specialization || '',
          bio: res.data.bio || '',
        })
      })
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false))
  }, [])

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await api.put('/trainers/profile', form)
      setProfile(prev => ({ ...prev, ...form }))
      updateUser({ first_name: form.first_name, last_name: form.last_name })
      toast.success('Profile updated')
      setEditing(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('New passwords do not match')
      return
    }
    if (pwForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters')
      return
    }
    setSavingPw(true)
    try {
      await api.put('/trainers/profile', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      })
      toast.success('Password changed successfully')
      setPwForm({ current_password: '', new_password: '', confirm_password: '' })
      setShowPwSection(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change password')
    } finally {
      setSavingPw(false)
    }
  }

  if (loading) return <LoadingSpinner label="Loading profile" />

  if (!profile) return (
    <div className="cinema-empty">
      <User size={32} className="mx-auto mb-3 text-cinema-muted/20" />
      <p className="font-sans text-sm text-cinema-muted">Could not load profile.</p>
    </div>
  )

  const inputCls = "w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-3 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors placeholder:text-cinema-muted/40 resize-none"
  const totalStudents = (profile.courses || []).reduce((sum, c) => sum + parseInt(c.enrolled_students || 0), 0)

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-0.5">Trainer Portal</span>
          <h1 className="font-display text-xl text-white font-light">My Profile</h1>
        </div>
      </div>

      {/* Profile hero */}
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
        <div className="w-20 h-20 bg-cinema-glow/15 border border-cinema-glow/30 flex items-center justify-center font-display text-3xl font-semibold text-cinema-glow shrink-0">
          {getInitials(profile.first_name, profile.last_name)}
        </div>
        <div className="flex-1 text-center sm:text-left">
          <h2 className="font-display text-xl text-white font-light">{profile.first_name} {profile.last_name}</h2>
          {profile.trainer_number && (
            <p className="font-sans text-xs text-cinema-muted mt-0.5 flex items-center gap-1 justify-center sm:justify-start">
              <Award size={11} /> {profile.trainer_number}
            </p>
          )}
          {profile.specialization && (
            <p className="font-sans text-xs text-cinema-glow/80 mt-1 flex items-center gap-1 justify-center sm:justify-start">
              <Briefcase size={11} /> {profile.specialization}
            </p>
          )}
          {profile.bio && <p className="font-sans text-xs text-cinema-muted mt-2 line-clamp-2 max-w-md">{profile.bio}</p>}
        </div>
        <button onClick={() => setEditing(!editing)}
          className="shrink-0 flex items-center gap-2 px-4 py-2 font-sans text-xs transition-colors cinema-btn-ghost min-h-0">
          {editing ? <><X size={12} /> Cancel</> : <><Edit2 size={12} /> Edit Profile</>}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-8">
        {[
          { label: 'Courses', value: profile.course_count || 0 },
          { label: 'Students', value: totalStudents },
          { label: 'Member Since', value: profile.joined_at ? new Date(profile.joined_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '—' },
        ].map(({ label, value }) => (
          <div key={label} className="cinema-stat">
            <span className="cinema-stat-label">{label}</span>
            <span className="cinema-stat-value">{value}</span>
          </div>
        ))}
      </div>

      {/* Edit form */}
      {editing && (
        <div className="pt-8 border-t border-white/5">
          <div className="cinema-section-header">
            <h2 className="cinema-section-title flex items-center gap-2">
              <Edit2 size={13} className="text-cinema-glow" /> Edit Profile
            </h2>
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="cinema-label block mb-1.5">First Name</label>
                <input type="text" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className={inputCls} required />
              </div>
              <div>
                <label className="cinema-label block mb-1.5">Last Name</label>
                <input type="text" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className={inputCls} required />
              </div>
            </div>
            <div>
              <label className="cinema-label block mb-1.5">Phone</label>
              <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="e.g. 0712345678" />
            </div>
            <div>
              <label className="cinema-label block mb-1.5">Specialization</label>
              <input type="text" value={form.specialization} onChange={e => setForm({ ...form, specialization: e.target.value })} className={inputCls} placeholder="e.g. Photography & Videography" />
            </div>
            <div>
              <label className="cinema-label block mb-1.5">Bio</label>
              <textarea rows={3} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} className={inputCls} placeholder="A short description about yourself..." />
            </div>
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={saving} className="cinema-btn-primary text-xs px-5 py-2.5 min-h-0 flex items-center gap-2 disabled:opacity-50">
                <Save size={13} /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" onClick={() => setEditing(false)} className="cinema-btn-ghost text-xs px-4 py-2.5 min-h-0">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Contact info */}
      <div className="pt-8 border-t border-white/5">
        <div className="cinema-section-header">
          <h2 className="cinema-section-title flex items-center gap-2">
            <User size={13} className="text-cinema-glow" /> Contact Information
          </h2>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3 font-sans text-sm text-white">
            <Mail size={13} className="text-cinema-muted shrink-0" /> {profile.email}
          </div>
          <div className="flex items-center gap-3 font-sans text-sm text-white">
            <Phone size={13} className="text-cinema-muted shrink-0" />
            {profile.phone || <span className="text-cinema-muted italic">No phone added</span>}
          </div>
        </div>
      </div>

      {/* Assigned courses */}
      {profile.courses && profile.courses.length > 0 && (
        <div className="pt-8 border-t border-white/5">
          <div className="cinema-section-header">
            <h2 className="cinema-section-title flex items-center gap-2">
              <BookOpen size={13} className="text-cinema-glow" /> Assigned Courses
            </h2>
          </div>
          <div className="cinema-list">
            {profile.courses.map(course => (
              <div key={course.id} className="cinema-list-row justify-between">
                <div>
                  <p className="font-sans text-sm sm:text-base text-white font-medium">{course.title}</p>
                  <p className="font-sans text-xs text-cinema-muted mt-0.5">{course.course_code}</p>
                </div>
                <span className="flex items-center gap-1.5 font-sans text-xs text-cinema-muted font-medium">
                  <Users size={13} /> {course.enrolled_students || 0} students
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Change password */}
      <div className="pt-8 border-t border-white/5">
        <div className="cinema-section-header">
          <h2 className="cinema-section-title flex items-center gap-2">
            <Lock size={13} className="text-cinema-glow" /> Change Password
          </h2>
          <button onClick={() => setShowPwSection(!showPwSection)} className="font-sans text-xs text-cinema-glow hover:text-cinema-gold transition-colors">
            {showPwSection ? 'Cancel' : 'Change'}
          </button>
        </div>

        {showPwSection ? (
          <form onSubmit={handlePasswordChange} className="space-y-4">
            {[
              { key: 'current', label: 'Current Password', field: 'current_password' },
              { key: 'new', label: 'New Password', field: 'new_password' },
              { key: 'confirm', label: 'Confirm New Password', field: 'confirm_password' },
            ].map(({ key, label, field }) => (
              <div key={key}>
                <label className="cinema-label block mb-1.5">{label}</label>
                <div className="relative">
                  <input type={showPw[key] ? 'text' : 'password'} value={pwForm[field]}
                    onChange={e => setPwForm({ ...pwForm, [field]: e.target.value })}
                    className={`${inputCls} pr-10`} required />
                  <button type="button" onClick={() => setShowPw(prev => ({ ...prev, [key]: !prev[key] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-cinema-muted hover:text-white transition-colors">
                    {showPw[key] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            ))}
            <button type="submit" disabled={savingPw} className="cinema-btn-primary text-xs px-5 py-2.5 min-h-0 flex items-center gap-2 disabled:opacity-50">
              <Lock size={13} /> {savingPw ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        ) : (
          <p className="font-sans text-xs text-cinema-muted">Keep your account secure by using a strong password.</p>
        )}
      </div>
    </div>
  )
}
